import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';

export interface SidebarDropdownOption {
  value: string;
  label: string;
  /** Optional inline style for the option label (e.g. fontFamily preview). */
  labelSx?: Record<string, unknown>;
}

export interface SidebarDropdownProps {
  value: string;
  options: SidebarDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  id?: string;
}

const COLORS = {
  border: '#B7D0EA',
  text: '#1B3A5C',
  selectedBg: '#E6F0FA',
  selectedText: '#2F7BC9',
  white: '#FFFFFF',
};

/**
 * Rounded select for the Widget Builder sidebar only.
 * Do not reuse on other screens.
 */
export default function SidebarDropdown({
  value,
  options,
  onChange,
  placeholder = 'Select',
  disabled = false,
  fullWidth = true,
  id,
}: SidebarDropdownProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const selected = options.find((opt) => opt.value === value);
  const displayLabel = selected?.label || placeholder;

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  const handleToggle = (): void => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleClose = (): void => setOpen(false);

  const handleSelect = (nextValue: string): void => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <Box id={id} sx={{ width: fullWidth ? '100%' : 'auto', position: 'relative' }}>
      <ButtonBase
        ref={anchorRef}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        sx={{
          width: fullWidth ? '100%' : 'auto',
          minHeight: 40,
          px: 1.75,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderRadius: '10px',
          border: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.white,
          color: COLORS.text,
          textAlign: 'left',
          opacity: disabled ? 0.55 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          '&:hover': {
            backgroundColor: disabled ? COLORS.white : '#F7FBFF',
          },
        }}
      >
        <Typography
          component="span"
          noWrap
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 500,
            color: COLORS.text,
            lineHeight: 1.3,
            ...(selected?.labelSx || {}),
          }}
        >
          {displayLabel}
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 20, color: COLORS.text, flexShrink: 0 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 20, color: COLORS.text, flexShrink: 0 }} />
        )}
      </ButtonBase>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ]}
        style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={0}
            role="listbox"
            sx={{
              width: '100%',
              borderRadius: '10px',
              backgroundColor: COLORS.white,
              boxShadow: '0 8px 24px rgba(27, 58, 92, 0.12)',
              border: `1px solid ${COLORS.border}`,
              py: 0.75,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ maxHeight: 280, overflowY: 'auto', px: 0.75 }}>
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <Box
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(option.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 1.5,
                      py: 1.1,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? COLORS.selectedBg : 'transparent',
                      '&:hover': {
                        backgroundColor: isSelected ? COLORS.selectedBg : '#F5F9FD',
                      },
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        fontWeight: isSelected ? 600 : 500,
                        color: isSelected ? COLORS.selectedText : COLORS.text,
                        lineHeight: 1.3,
                        ...(option.labelSx || {}),
                      }}
                    >
                      {option.label}
                    </Typography>
                    {isSelected && (
                      <CheckIcon sx={{ fontSize: 18, color: COLORS.selectedText, flexShrink: 0 }} />
                    )}
                  </Box>
                );
              })}
              {options.length === 0 && (
                <Typography sx={{ px: 1.5, py: 1.25, fontSize: 13, color: 'text.secondary' }}>
                  No options
                </Typography>
              )}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
